import { useEffect, useRef } from 'react';
import { useMap, useElement, useLayers } from '@ais-ui/react-openlayers';
import { type RadarOverlayInfo } from '@ais-ui/websockets';
import Layer from 'ol/layer/Layer';
import { fromLonLat, getPointResolution } from 'ol/proj';
import type { FrameState } from 'ol/Map';
import type { Coordinate } from 'ol/coordinate';

/**
 * Request size sent to the server. The SPx server caps the returned image
 * at its configured maximum (typically 1024). We request larger so the
 * server always returns its maximum resolution.
 */
const RADAR_IMG_REQUEST_SIZE = 2048;

/**
 * Assumed server-side maximum image dimension. When the viewport exceeds
 * this, we increase the requested mpp so the returned image still covers
 * the full viewport geographic extent.
 */
const SERVER_MAX_PIXELS = 1024;

/** How often a fresh radar frame is requested while the view is still. */
const RADAR_REFRESH_INTERVAL_MS = 1000;

/** A radar frame still downloading after this long is cancelled. */
const RADAR_IMG_TIMEOUT_MS = 5000;

/**
 * Stops an image download. Only settling the result (e.g. ignoring it via an
 * abort flag) leaves the browser fetching and decoding it anyway. Handlers go
 * first so the cancellation itself is silent.
 */
function cancelImageLoad(image: HTMLImageElement) {
  image.onload = null;
  image.onerror = null;
  image.removeAttribute('src');
}

interface CambridgePixelRadarLayerProps {
  radarInfo: RadarOverlayInfo;
  initialHidden?: boolean;
}

/** State captured when a radar image was fetched. */
interface FetchState {
  mpp: number;
  w: number;
  h: number;
}

/**
 * Renders a Cambridge Pixel radar overlay on the map.
 *
 * The SPx server always centres the radar scan in the returned image
 * regardless of `ll`, so we omit `ll` (server defaults to the radar's
 * physical position) and query that position via WebSocket to geo-pin
 * the image at a fixed location. This eliminates drift during pan/zoom.
 */
export function CambridgePixelRadarLayer({
  radarInfo,
  initialHidden,
}: CambridgePixelRadarLayerProps) {
  const map = useMap();
  const { toggleLayerVisibility } = useLayers();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const fetchStateRef = useRef<FetchState | null>(null);
  const radarPosRef = useRef<Coordinate | null>(null);
  const opacityRef = useRef(radarInfo.opacity);
  opacityRef.current = radarInfo.opacity;

  const layerRef = useRef(
    new Layer({
      render(frameState: FrameState) {
        if (!canvasRef.current) {
          canvasRef.current = document.createElement('canvas');
          canvasRef.current.style.position = 'absolute';
          canvasRef.current.style.left = '0';
          canvasRef.current.style.top = '0';
        }
        const canvas = canvasRef.current;
        const img = imageRef.current;
        const fs = fetchStateRef.current;
        const radarPos = radarPosRef.current;
        const { size, pixelRatio } = frameState;

        const cw = Math.round(size[0] * pixelRatio);
        const ch = Math.round(size[1] * pixelRatio);
        if (canvas.width !== cw || canvas.height !== ch) {
          canvas.width = cw;
          canvas.height = ch;
          canvas.style.width = Math.round(size[0]) + 'px';
          canvas.style.height = Math.round(size[1]) + 'px';
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return canvas;
        ctx.clearRect(0, 0, cw, ch);

        if (!img || !fs || !radarPos) return canvas;

        const curCenter = frameState.viewState.center;
        const curRes = frameState.viewState.resolution;

        // Screen pixels per image pixel at current zoom
        const curMpp = getPointResolution('EPSG:3857', curRes, radarPos);
        const scale = fs.mpp / curMpp;

        // Radar centre position in canvas pixels
        const rx =
          ((radarPos[0] - curCenter[0]) / curRes) * pixelRatio + cw / 2;
        const ry =
          ((curCenter[1] - radarPos[1]) / curRes) * pixelRatio + ch / 2;

        // Draw image centred on the radar's fixed position
        const imgW = fs.w * scale * pixelRatio;
        const imgH = fs.h * scale * pixelRatio;

        ctx.globalAlpha = opacityRef.current;
        ctx.drawImage(img, rx - imgW / 2, ry - imgH / 2, imgW, imgH);

        return canvas;
      },
    }),
  );

  useElement(
    layerRef.current,
    radarInfo.name,
    true,
    false,
    {
      type: 'cambridge-radar',
      radarId: radarInfo.id,
    },
    radarInfo.id,
  );

  // Restore hidden state for favorite radar layers that were saved as not visible
  const hasRestoredVisibility = useRef(false);
  useEffect(() => {
    if (initialHidden && layerRef.current && !hasRestoredVisibility.current) {
      toggleLayerVisibility(layerRef.current, false);
      hasRestoredVisibility.current = true;
    }
  }, [initialHidden, toggleLayerVisibility]);

  useEffect(() => {
    layerRef.current.setOpacity(radarInfo.opacity);
  }, [radarInfo.opacity]);

  // Query radar position via SPx WebSocket command interface
  useEffect(() => {
    let ws: WebSocket | null = null;
    try {
      const parsed = new URL(radarInfo.url);
      const proto = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
      ws = new WebSocket(`${proto}//${parsed.host}`, ['spx-command']);
    } catch {
      return;
    }

    const socket = ws;

    socket.onopen = () => {
      const cmd = 'get WebServer LatLong';
      const len = new TextEncoder().encode(cmd).length;
      socket.send(`${len + 1} ${cmd}`);
    };

    socket.onmessage = (event: MessageEvent) => {
      const raw = String(event.data);
      const colon = raw.indexOf(':');
      if (colon < 0 || raw.substring(0, colon) !== 'ok') return;
      const parts = raw
        .substring(colon + 1)
        .trim()
        .split(/\s+/);
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lon = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lon)) {
          radarPosRef.current = fromLonLat([lon, lat]);
          layerRef.current.changed();
        }
      }
      socket.close();
    };

    socket.onerror = () => socket.close();

    return () => {
      if (socket.readyState <= WebSocket.OPEN) socket.close();
    };
  }, [radarInfo.url]);

  // Fetch radar image on moveend (and once on mount), then refresh it every
  // second. At most one request is in flight: a refresh tick waits for it, and
  // a move, timeout or unmount cancels its download outright, so a slow or
  // hung SPx server can't pile up requests.
  useEffect(() => {
    if (!map) return;

    let inFlight: {
      image: HTMLImageElement;
      timeoutId: ReturnType<typeof setTimeout>;
    } | null = null;

    const cancelInFlight = () => {
      if (!inFlight) return;
      clearTimeout(inFlight.timeoutId);
      cancelImageLoad(inFlight.image);
      inFlight = null;
    };

    const fetchRadarImage = () => {
      cancelInFlight();

      const view = map.getView();
      const size = map.getSize();
      if (!view || !size) return;

      const center = view.getCenter();
      const resolution = view.getResolution();
      if (!center || !resolution) return;

      const mppPoint = radarPosRef.current ?? center;
      const viewMpp = getPointResolution('EPSG:3857', resolution, mppPoint);

      // The image is centred on the radar, not the viewport centre.
      // Compute the needed image half-extent to cover the full viewport
      // by accounting for the radar's offset from the viewport centre.
      let neededCSSPx: number;
      if (radarPosRef.current) {
        const offX = Math.abs(radarPosRef.current[0] - center[0]) / resolution;
        const offY = Math.abs(radarPosRef.current[1] - center[1]) / resolution;
        neededCSSPx = Math.max(size[0] + 2 * offX, size[1] + 2 * offY);
      } else {
        neededCSSPx = Math.max(size[0], size[1]);
      }

      const mpp =
        neededCSSPx > SERVER_MAX_PIXELS
          ? (neededCSSPx * viewMpp) / SERVER_MAX_PIXELS
          : viewMpp;

      // Omit ll — server defaults to the radar's physical position.
      // Request a large square; the server will cap at its own maximum.
      const url = `${radarInfo.url}/radar.png?mpp=${mpp}&wpixels=${RADAR_IMG_REQUEST_SIZE}&hpixels=${RADAR_IMG_REQUEST_SIZE}&t=${Date.now()}`;

      const img = new Image();
      const settle = () => {
        if (inFlight?.image !== img) return;
        clearTimeout(inFlight.timeoutId);
        inFlight = null;
      };
      img.onload = () => {
        settle();
        imageRef.current = img;
        // Use actual returned dimensions (server may cap the requested size)
        fetchStateRef.current = {
          mpp,
          w: img.naturalWidth,
          h: img.naturalHeight,
        };
        layerRef.current.changed();
      };
      img.onerror = settle;
      inFlight = {
        image: img,
        timeoutId: setTimeout(cancelInFlight, RADAR_IMG_TIMEOUT_MS),
      };
      img.src = url;
    };

    const refreshRadarImage = () => {
      if (!inFlight) fetchRadarImage();
    };

    fetchRadarImage();
    map.on('moveend', fetchRadarImage);
    const interval = setInterval(refreshRadarImage, RADAR_REFRESH_INTERVAL_MS);

    return () => {
      cancelInFlight();
      map.un('moveend', fetchRadarImage);
      clearInterval(interval);
    };
  }, [map, radarInfo.url]);

  return null;
}
